'use client';

import { useState, useEffect } from 'react';
import { BrainCircuitIcon } from '@/components/shared/AIComponents';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface DemoClient {
  name: string;
  pan: string;
  status: 'critical' | 'attention' | 'good';
  issue: string;
}

interface DemoReport {
  client: string;
  status: 'critical' | 'attention' | 'good';
  summary: string;
  sections: {
    title: string;
    status: 'critical' | 'attention' | 'good';
    items: string[];
  }[];
  recommendations: string[];
}

const demoClients: DemoClient[] = [
  { name: 'Acme Industries', pan: 'XXXXA1234X', status: 'critical', issue: '15 overdue notices' },
  { name: 'Global Tech Pvt', pan: 'XXXXG5678Y', status: 'critical', issue: '9 overdue notices' },
  { name: 'Metro Corp', pan: 'XXXXM9012Z', status: 'attention', issue: 'Demand pending' },
  { name: 'Prime Solutions', pan: 'XXXXP3456A', status: 'good', issue: '' },
];

const demoReports: Record<string, DemoReport> = {
  'Acme Industries': {
    client: 'Acme Industries',
    status: 'critical',
    summary: 'Critical tax compliance issues with Rs.5.99 crore in outstanding demands and 15 overdue notice responses requiring immediate attention.',
    sections: [
      {
        title: 'Tax Filings',
        status: 'good',
        items: ['ITR-6 filed consistently for AY 2022-23 to AY 2025-26', 'All returns filed within due dates'],
      },
      {
        title: 'Tax Demands',
        status: 'critical',
        items: ['Total outstanding: Rs.5,98,54,987 across 2 AYs', 'AY 2019-20: Rs.5,39,22,258 under Section 147'],
      },
      {
        title: 'Notices & Proceedings',
        status: 'critical',
        items: ['15 notices pending response', '8 notices past due date', 'Sections: 143(1), 148A, 142(1)'],
      },
    ],
    recommendations: [
      'Immediately respond to 8 overdue notices to avoid penalties',
      'File rectification for AY 2019-20 demand of Rs.5.39 crore',
      'Prioritize 148A notices - reopening assessment proceedings',
    ],
  },
  'Global Tech Pvt': {
    client: 'Global Tech Pvt',
    status: 'critical',
    summary: '9 overdue tax notices requiring urgent attention, with potential penalties accumulating daily.',
    sections: [
      {
        title: 'Tax Filings',
        status: 'good',
        items: ['ITR-3 filed for all assessment years', 'No pending return filings'],
      },
      {
        title: 'Tax Demands',
        status: 'attention',
        items: ['Outstanding demand: Rs.1,25,000', 'Under Section 143(1) adjustment'],
      },
      {
        title: 'Notices & Proceedings',
        status: 'critical',
        items: ['9 notices overdue', '3 under Section 142(1)', 'Response deadline passed'],
      },
    ],
    recommendations: [
      'Submit responses to 142(1) notices within 48 hours',
      'Contest Section 143(1) demand through rectification',
      'Schedule client meeting to gather required documents',
    ],
  },
};

const statusConfig = {
  critical: {
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500',
    icon: ExclamationCircleIcon,
  },
  attention: {
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    icon: ExclamationTriangleIcon,
  },
  good: {
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    icon: CheckCircleIcon,
  },
};

export default function FinkInsightsDemoSection() {
  const [selectedClient, setSelectedClient] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showReport, setShowReport] = useState(true);

  const currentClient = demoClients[selectedClient];
  const currentReport = demoReports[currentClient.name];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setShowReport(false);

      setTimeout(() => {
        setSelectedClient((prev) => (prev + 1) % 2); // Only cycle through first 2 clients with reports
        setTimeout(() => {
          setShowReport(true);
          setIsAnimating(false);
        }, 300);
      }, 300);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const config = statusConfig[currentClient.status];
  const StatusIcon = config.icon;

  return (
    <section className="py-12 lg:py-24 bg-gradient-to-br from-violet-50 via-white to-indigo-50 overflow-hidden relative">
      {/* Floating Card - Top Right */}
      <div className="absolute top-8 right-8 hidden xl:block">
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-violet-100 p-4 w-64 transform rotate-3 hover:rotate-0 transition-transform duration-300">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <BrainCircuitIcon className="w-4 h-4 text-white" animate />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-900">Quick Health Check</p>
              <p className="text-[10px] text-gray-500">AI-powered analysis</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Portfolio Status</span>
              <span className="flex items-center gap-1 text-amber-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Needs Review
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Pending Actions</span>
              <span className="text-red-600 font-semibold">24</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Clients at Risk</span>
              <span className="text-red-600 font-semibold">2</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-[10px] text-violet-600 font-medium flex items-center gap-1">
              <BrainCircuitIcon className="w-3 h-3" animate />
              View full insights →
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-violet-100 border border-violet-200 rounded-full mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500"></span>
            </span>
            <span className="text-xs lg:text-sm font-medium text-violet-700">Live Demo</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 lg:mb-4 px-4">
            Fink AI Insights
          </h2>
          <p className="text-sm lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Instant AI-powered health reports for every client. See who needs attention at a glance.
          </p>
        </div>

        {/* Demo Card */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-600 px-4 py-3 lg:px-6 lg:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 lg:gap-3">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <BrainCircuitIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" animate />
                  </div>
                  <div>
                    <h3 className="text-sm lg:text-base font-semibold text-white">Fink AI Insights</h3>
                    <p className="text-[10px] lg:text-xs text-white/70">AI-powered portfolio analysis</p>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 lg:gap-4 text-white/80 text-xs lg:text-sm">
                  <span className="flex items-center gap-1 lg:gap-1.5">
                    <span className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-red-400"></span>
                    2 Critical
                  </span>
                  <span className="flex items-center gap-1 lg:gap-1.5">
                    <span className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-amber-400"></span>
                    1 Attention
                  </span>
                  <span className="flex items-center gap-1 lg:gap-1.5">
                    <span className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-emerald-400"></span>
                    1 Good
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col lg:flex-row min-h-[350px] lg:min-h-[450px]">
              {/* Client List - Horizontal scroll on mobile, vertical list on desktop */}
              <div className="lg:w-64 border-b lg:border-b-0 lg:border-r border-gray-100 bg-gray-50/50">
                {/* Search - hidden on mobile */}
                <div className="hidden lg:block p-3 border-b border-gray-100">
                  <div className="bg-white rounded-lg px-3 py-2 text-sm text-gray-400 border border-gray-200">
                    Search clients...
                  </div>
                </div>
                {/* Client list - horizontal scroll on mobile */}
                <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible scrollbar-hide divide-x lg:divide-x-0 lg:divide-y divide-gray-100">
                  {demoClients.map((client, index) => {
                    const clientConfig = statusConfig[client.status];
                    const isSelected = selectedClient === index;
                    return (
                      <div
                        key={client.pan}
                        onClick={() => {
                          if (demoReports[client.name]) {
                            setShowReport(false);
                            setTimeout(() => {
                              setSelectedClient(index);
                              setShowReport(true);
                            }, 200);
                          }
                        }}
                        className={`
                          flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 cursor-pointer transition-all flex-shrink-0 touch-manipulation
                          ${isSelected ? 'bg-violet-50 border-b-2 lg:border-b-0 lg:border-l-2 border-violet-500' : 'hover:bg-gray-50 border-b-2 lg:border-b-0 lg:border-l-2 border-transparent'}
                        `}
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${clientConfig.dot}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs lg:text-sm font-medium truncate ${isSelected ? 'text-violet-900' : 'text-gray-900'}`}>
                            {client.name}
                          </p>
                          <p className="text-[10px] lg:text-xs text-gray-400 hidden lg:block">{client.pan}</p>
                        </div>
                        {client.issue && (
                          <span className={`hidden lg:inline text-[10px] px-1.5 py-0.5 rounded ${clientConfig.bg} ${clientConfig.color} whitespace-nowrap`}>
                            {client.issue}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Health Report */}
              <div className="flex-1 p-4 lg:p-6 overflow-y-auto">
                {currentReport ? (
                  <div className={`transition-all duration-300 ${showReport ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    {/* Report Header */}
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-2 mb-3 lg:mb-4">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg ${config.bg} ${config.border} border flex items-center justify-center`}>
                          <StatusIcon className={`w-4 h-4 lg:w-5 lg:h-5 ${config.color}`} />
                        </div>
                        <div>
                          <h4 className="text-base lg:text-lg font-bold text-gray-900">{currentReport.client}</h4>
                          <span className={`text-[10px] lg:text-xs px-1.5 lg:px-2 py-0.5 rounded-full ${config.bg} ${config.color} font-medium`}>
                            {currentClient.status === 'critical' ? 'Critical' : currentClient.status === 'attention' ? 'Needs Attention' : 'Good'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 lg:gap-2 px-2 py-1 bg-violet-50 border border-violet-100 rounded-lg">
                        <BrainCircuitIcon className="w-3 h-3 text-violet-600" animate />
                        <span className="text-[9px] lg:text-[10px] text-violet-700 font-medium">AI Generated</span>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className={`mb-3 lg:mb-4 text-xs lg:text-sm text-gray-700 ${config.bg} rounded-lg p-2.5 lg:p-3 border-l-4 ${config.border.replace('border-', 'border-l-')}`}>
                      {currentReport.summary}
                    </div>

                    {/* Sections Grid - Stack on mobile, 3 columns on desktop */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 lg:gap-3 mb-3 lg:mb-4">
                      {currentReport.sections.map((section, i) => {
                        const sectionConfig = statusConfig[section.status];
                        return (
                          <div key={i} className={`p-2.5 lg:p-3 rounded-lg border ${sectionConfig.border} ${sectionConfig.bg}`}>
                            <div className="flex items-center gap-2 mb-1.5 lg:mb-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${sectionConfig.dot}`} />
                              <h5 className="text-[10px] lg:text-xs font-semibold text-gray-900">{section.title}</h5>
                            </div>
                            <ul className="space-y-0.5 lg:space-y-1">
                              {section.items.slice(0, 2).map((item, j) => (
                                <li key={j} className="text-[10px] lg:text-[11px] text-gray-600 leading-relaxed">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>

                    {/* Recommendations */}
                    <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg p-2.5 lg:p-3 border border-violet-100">
                      <div className="flex items-center gap-2 mb-1.5 lg:mb-2">
                        <BrainCircuitIcon className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-violet-600" animate />
                        <h5 className="text-[10px] lg:text-xs font-semibold text-gray-900">AI Recommendations</h5>
                      </div>
                      <div className="space-y-1 lg:space-y-1.5">
                        {currentReport.recommendations.map((rec, i) => (
                          <div key={i} className="flex items-start gap-1.5 lg:gap-2 text-[10px] lg:text-[11px] text-gray-700">
                            <span className="flex-shrink-0 w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-full bg-violet-500 text-white text-[8px] lg:text-[9px] font-bold flex items-center justify-center mt-0.5">
                              {i + 1}
                            </span>
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <p className="text-xs lg:text-sm">Select a client to view health report</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Demo indicator dots */}
          <div className="flex justify-center gap-2 mt-4 lg:mt-6">
            {[0, 1].map((i) => (
              <button
                key={i}
                onClick={() => {
                  setShowReport(false);
                  setTimeout(() => {
                    setSelectedClient(i);
                    setShowReport(true);
                  }, 200);
                }}
                className={`h-2 rounded-full transition-all touch-manipulation ${i === selectedClient ? 'w-8 bg-violet-500' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
